import { z } from "zod";
import { showConfigSchema } from "./config.js";
import { particleSchema } from "./particle.js";
import { stageStateSchema } from "./stage.js";

/**
 * The full show state. Everything the projection renders must be derivable from this
 * object and nothing else — that is what makes an F5, a PC reboot or a reconnect at
 * the venue a non-event.
 */
export const snapshotSchema = z.object({
  protocolVersion: z.number().int(),
  /**
   * New value per server process. A client seeing a different one than last time
   * knows the server restarted; the snapshot has already repaired its state.
   */
  serverSessionId: z.string().uuid(),
  /** Incremented by every admin reset. Distinguishes "restarted" from "wiped". */
  resetCount: z.number().int().nonnegative(),
  /** Server clock at send time. Clients derive their offset from this, never Date.now(). */
  serverTime: z.number().int(),
  /** Highest seq the server has emitted; clients use it for gap detection. */
  seq: z.number().int().nonnegative(),
  config: showConfigSchema,
  stage: stageStateSchema,
  round: z.number().int().nonnegative(),
  /** All live particles, ordered by seq ascending. */
  particles: z.array(particleSchema),
  /** Lifetime counter across rounds. */
  totalParticles: z.number().int().nonnegative(),
  /**
   * initial = fresh connect or F5, resync = reconnect after a drop, forced = the
   * client asked. All three restore silently; only the logging differs.
   */
  reason: z.enum(["initial", "resync", "forced"]),
});
export type SnapshotPayload = z.infer<typeof snapshotSchema>;
